<template>
  <div class="activity-center-preferences">
    <div class="activity-center-preferences__header">
      <span class="activity-center-preferences__title">Notification Preferences</span>
      <mi-btn
        flat
        dense
        no-hover
        icon-type
        icon="close"
        icon-size="16px"
        class="activity-center-preferences__close-btn"
        @click="$emit('close')"
      ></mi-btn>
    </div>

    <div class="activity-center-preferences__body">
      <div class="activity-center-preferences__item">
        <div class="activity-center-preferences__item-header">
          <span class="activity-center-preferences__item-label">
            Activity notifications
          </span>
          <label
            class="activity-center-preferences__switch"
            for="pref-activity-notifications"
          >
            <input
              id="pref-activity-notifications"
              type="checkbox"
              :checked="preferences.activityNotifications"
              class="activity-center-preferences__switch-input"
              @change="toggleActivityNotifications($event.target.checked)"
            />
            <span
              class="activity-center-preferences__switch-slider"
              :class="{
                'activity-center-preferences__switch-slider--active':
                  preferences.activityNotifications,
              }"
            ></span>
          </label>
        </div>
        <span class="activity-center-preferences__item-description">
          Receive notifications about job activity
        </span>
      </div>

      <div class="activity-center-preferences__divider"></div>

      <div class="activity-center-preferences__sub-items">
        <div class="activity-center-preferences__item">
          <div class="activity-center-preferences__item-header">
            <span class="activity-center-preferences__item-label">
              Failed jobs
            </span>
            <label
              class="activity-center-preferences__switch"
              for="pref-failed-jobs"
            >
              <input
                id="pref-failed-jobs"
                type="checkbox"
                :checked="preferences.failedJobs"
                :disabled="!preferences.activityNotifications"
                class="activity-center-preferences__switch-input"
                @change="toggleFailedJobs($event.target.checked)"
              />
              <span
                class="activity-center-preferences__switch-slider"
                :class="{
                  'activity-center-preferences__switch-slider--active':
                    preferences.failedJobs,
                  'activity-center-preferences__switch-slider--disabled':
                    !preferences.activityNotifications,
                }"
              ></span>
            </label>
          </div>
          <span class="activity-center-preferences__item-description">
            Notify me when a job fails
          </span>
        </div>

        <div class="activity-center-preferences__item">
          <div class="activity-center-preferences__item-header">
            <span class="activity-center-preferences__item-label">
              Completed jobs
            </span>
            <label
              class="activity-center-preferences__switch"
              for="pref-completed-jobs"
            >
              <input
                id="pref-completed-jobs"
                type="checkbox"
                :checked="preferences.completedJobs"
                :disabled="!preferences.activityNotifications"
                class="activity-center-preferences__switch-input"
                @change="toggleCompletedJobs($event.target.checked)"
              />
              <span
                class="activity-center-preferences__switch-slider"
                :class="{
                  'activity-center-preferences__switch-slider--active':
                    preferences.completedJobs,
                  'activity-center-preferences__switch-slider--disabled':
                    !preferences.activityNotifications,
                }"
              ></span>
            </label>
          </div>
          <span class="activity-center-preferences__item-description">
            Notify me when a job completes
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import useActivityCenterPreferences
    from '@/composables/useActivityCenterPreferences'

  export default {
    name: 'ActivityCenterPreferences',
    emits: ['close'],
    setup() {
      const {
        preferences,
        toggleActivityNotifications,
        toggleFailedJobs,
        toggleCompletedJobs
      } = useActivityCenterPreferences()

      return {
        preferences,
        toggleActivityNotifications,
        toggleFailedJobs,
        toggleCompletedJobs
      }
    }
  }
</script>

<style lang="scss" scoped>
  .activity-center-preferences {
    position: absolute;
    top: 72px;
    right: 32px;
    width: 307px;
    padding: 20px;
    background-color: $mi-white;
    border: 1px solid $mi-anthracite-200;
    border-radius: 8px;
    z-index: 10;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    &__title {
      font-family: $mi-typography-font-family;
      font-weight: 700;
      font-size: 14px;
      line-height: 17px;
      color: $mi-anthracite-800;
    }

    &__close-btn {
      color: $mi-anthracite-800;
    }

    &__body {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__divider {
      width: 100%;
      height: 1px;
      background-color: $mi-anthracite-200;
      margin: 4px 0;
    }

    &__sub-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding-left: 12px;
    }

    &__item {
      display: flex;
      flex-direction: column;
      padding: 8px 0;
    }

    &__item-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__item-label {
      font-family: $mi-typography-font-family;
      font-weight: 700;
      font-size: 14px;
      line-height: 17px;
      color: $mi-anthracite-800;
    }

    &__item-description {
      font-family: $mi-typography-font-family;
      font-weight: 400;
      font-size: 12px;
      line-height: 1.5em;
      color: $mi-anthracite-600;
      margin-top: 4px;
    }

    &__switch {
      position: relative;
      display: inline-block;
      width: 40px;
      height: 20px;
      cursor: pointer;
    }

    &__switch-input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    &__switch-slider {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: $mi-anthracite-200;
      border-radius: 24px;
      transition: background-color .2s;

      &:before {
        content: "";
        position: absolute;
        height: 16px;
        width: 16px;
        left: 2px;
        bottom: 2px;
        background-color: $mi-white;
        border-radius: 50%;
        transition: transform .2s;
      }

      &--active {
        background-color: #e40045;

        &:before {
          transform: translateX(20px);
        }
      }

      &--disabled {
        opacity: .5;
        cursor: not-allowed;
      }
    }
  }
</style>
